import { type LangLocale, type TranslateOptions } from '@myre/common-i18n';
import { type Scope } from '@myre/domain-i18n-core';

const importMap = {
  'accounting/en': () => import('@myre/domain-i18n-wordings/accounting/en'),
  'accounting/fr': () => import('@myre/domain-i18n-wordings/accounting/fr'),
  'accounting/de': () => import('@myre/domain-i18n-wordings/accounting/de'),
  'accounting/es': () => import('@myre/domain-i18n-wordings/accounting/es'),
  'accounting/en_uk': () =>
    import('@myre/domain-i18n-wordings/accounting/en_uk'),
  'am/en': () => import('@myre/domain-i18n-wordings/am/en'),
  'am/en-vad': () => import('@myre/domain-i18n-wordings/am/en-vad'),
  'am/en-tenant': () => import('@myre/domain-i18n-wordings/am/en-tenant'),
  'am/fr': () => import('@myre/domain-i18n-wordings/am/fr'),
  'am/fr-vad': () => import('@myre/domain-i18n-wordings/am/fr-vad'),
  'am/fr-tenant': () => import('@myre/domain-i18n-wordings/am/fr-tenant'),
  'am/de': () => import('@myre/domain-i18n-wordings/am/de'),
  'am/de-vad': () => import('@myre/domain-i18n-wordings/am/de-vad'),
  'am/de-tenant': () => import('@myre/domain-i18n-wordings/am/de-tenant'),
  'am/es': () => import('@myre/domain-i18n-wordings/am/es'),
  'am/es-vad': () => import('@myre/domain-i18n-wordings/am/es-vad'),
  'am/es-tenant': () => import('@myre/domain-i18n-wordings/am/es-tenant'),
  'am/en_uk': () => import('@myre/domain-i18n-wordings/am/en_uk'),
  'am/en_uk-vad': () => import('@myre/domain-i18n-wordings/am/en_uk-vad'),
  'am/en_uk-tenant': () => import('@myre/domain-i18n-wordings/am/en_uk-tenant'),
  'bp/en': () => import('@myre/domain-i18n-wordings/bp/en'),
  'bp/fr': () => import('@myre/domain-i18n-wordings/bp/fr'),
  'bp/de': () => import('@myre/domain-i18n-wordings/bp/de'),
  'bp/es': () => import('@myre/domain-i18n-wordings/bp/es'),
  'bp/en_uk': () => import('@myre/domain-i18n-wordings/bp/en_uk'),
  'invoicing/en': () => import('@myre/domain-i18n-wordings/invoicing/en'),
  'invoicing/en-tenant': () =>
    import('@myre/domain-i18n-wordings/invoicing/en-tenant'),
  'invoicing/fr': () => import('@myre/domain-i18n-wordings/invoicing/fr'),
  'invoicing/fr-tenant': () =>
    import('@myre/domain-i18n-wordings/invoicing/fr-tenant'),
  'invoicing/de': () => import('@myre/domain-i18n-wordings/invoicing/de'),
  'invoicing/de-tenant': () =>
    import('@myre/domain-i18n-wordings/invoicing/de-tenant'),
  'invoicing/es': () => import('@myre/domain-i18n-wordings/invoicing/es'),
  'invoicing/es-tenant': () =>
    import('@myre/domain-i18n-wordings/invoicing/es-tenant'),
  'invoicing/en_uk': () => import('@myre/domain-i18n-wordings/invoicing/en_uk'),
  'invoicing/en_uk-tenant': () =>
    import('@myre/domain-i18n-wordings/invoicing/en_uk-tenant'),
  'reporting/en': () => import('@myre/domain-i18n-wordings/reporting/en'),
  'reporting/fr': () => import('@myre/domain-i18n-wordings/reporting/fr'),
  'reporting/de': () => import('@myre/domain-i18n-wordings/reporting/de'),
  'reporting/es': () => import('@myre/domain-i18n-wordings/reporting/es'),
  'reporting/en_uk': () => import('@myre/domain-i18n-wordings/reporting/en_uk'),
  'system/en': () => import('@myre/domain-i18n-wordings/system/en'),
  'system/fr': () => import('@myre/domain-i18n-wordings/system/fr'),
  'system/de': () => import('@myre/domain-i18n-wordings/system/de'),
  'system/es': () => import('@myre/domain-i18n-wordings/system/es'),
  'system/en_uk': () => import('@myre/domain-i18n-wordings/system/en_uk'),
  'ui/en': () => import('@myre/domain-i18n-wordings/ui/en'),
  'ui/fr': () => import('@myre/domain-i18n-wordings/ui/fr'),
  'ui/de': () => import('@myre/domain-i18n-wordings/ui/de'),
  'ui/es': () => import('@myre/domain-i18n-wordings/ui/es'),
  'ui/en_uk': () => import('@myre/domain-i18n-wordings/ui/en_uk'),
  'ui-lib/en': () => import('@myre/domain-i18n-wordings/ui-lib/en'),
  'ui-lib/fr': () => import('@myre/domain-i18n-wordings/ui-lib/fr'),
  'ui-lib/de': () => import('@myre/domain-i18n-wordings/ui-lib/de'),
  'ui-lib/es': () => import('@myre/domain-i18n-wordings/ui-lib/es'),
  'ui-lib/en_uk': () => import('@myre/domain-i18n-wordings/ui-lib/en_uk'),
  'user/en': () => import('@myre/domain-i18n-wordings/user/en'),
  'user/fr': () => import('@myre/domain-i18n-wordings/user/fr'),
  'user/de': () => import('@myre/domain-i18n-wordings/user/de'),
  'user/es': () => import('@myre/domain-i18n-wordings/user/es'),
  'user/en_uk': () => import('@myre/domain-i18n-wordings/user/en_uk'),
};

export async function importI18n(
  scope: Scope,
  langLocale: LangLocale,
  callback: (ret: {
    wording: any;
    registerCallback: (
      cb: (str: string, options: TranslateOptions) => string,
    ) => void;
  }) => any,
  options: {
    variant?: string;
    withFrontendLibs?: boolean;
  } = {},
): Promise<any> {
  const { withFrontendLibs, variant } = options;

  const langKey = variant ? `${langLocale}-${variant}` : langLocale;

  const scopeKey = scope && `${scope}/${langKey}`;
  const systemKey = `system/${langLocale}`;
  const uiLibKey = withFrontendLibs && `ui-lib/${langLocale}`;
  const uiKey = withFrontendLibs && `ui/${langLocale}`;

  const keys = [
    systemKey,
    uiLibKey,
    uiKey,
    // scope should always be last to avoid collisions
    scopeKey,
  ].filter(Boolean);

  const wordings = await Promise.all(
    // @ts-expect-error
    keys.map(k => importMap[k]().then(r => callback(r))),
  );

  return wordings.reduce((acc: any, item: any) => Object.assign(acc, item), {});
}
